import player from 'lottie-web';
import { provideLottieOptions } from 'ngx-lottie';
import { ApplicationConfig, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { routes } from './app.routes';
import { registerLocaleData } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { Observable, catchError, firstValueFrom, forkJoin, map, of } from 'rxjs';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import localeEs from '@angular/common/locales/es';
import { errorInterceptor } from './core/interceptor/error.interceptor';
import { languageInterceptor } from './core/interceptor/language.interceptor';
import { LanguageService } from './core/services/language.service';
import { WorkflowFormDefaultsService } from './workflows/services/workflow-form-defaults.service';

class MultiHttpLoader implements TranslateLoader {
	constructor(private http: HttpClient, public resources: string[]) {}

	getTranslation(lang: string): Observable<any> {
		const requests = this.resources.map((path) =>
			this.http.get(`${path}${lang}.json`).pipe(catchError(() => of({}))),
		);
		return forkJoin(requests).pipe(map((response) => this.deepMerge({}, ...response)));
	}

	private deepMerge(target: any, ...sources: any[]): any {
		for (const source of sources) {
			for (const key of Object.keys(source || {})) {
				if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
					target[key] = this.deepMerge(target[key] || {}, source[key]);
				} else {
					target[key] = source[key];
				}
			}
		}
		return target;
	}
}

registerLocaleData(localeEs);
const httpLoaderFactory: (http: HttpClient) => MultiHttpLoader = (http: HttpClient) =>
	new MultiHttpLoader(http, [
		'/i18n/', // Ruta de la app
		'/assets/i18n/', // Ruta de la librería
	]);

export const appConfig: ApplicationConfig = {
	providers: [
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideRouter(routes),
		provideHttpClient(withInterceptors([languageInterceptor, errorInterceptor])),
		provideTranslateService({
			lang: 'es',
			fallbackLang: 'es',
			loader: {
				provide: TranslateLoader,
				useFactory: httpLoaderFactory,
				deps: [HttpClient],
			},
		}),
		provideAppInitializer(() => {
			const translate = inject(TranslateService);
			const language = inject(LanguageService);
			const workflowDefaults = inject(WorkflowFormDefaultsService);
			language.autoResolveLanguage();
			return firstValueFrom(
				forkJoin({
					lang: translate.use(language.getLanguage()),
					wfDefaults: workflowDefaults.preload(),
				}),
			);
		}),
		provideNativeDateAdapter(),
		{ provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
		provideLottieOptions({
			player: () => player,
		}),
	],
};
